import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { SyncIcon, TrashIcon, PlusCircleIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { AllowlistEntry, AuditLogEntry, TrialEntry } from '@app/Settings/types';
import {
  getAllowlist,
  getAllowlistEnabled,
  setAllowlistEnabled,
  addAllowlistEntry,
  removeAllowlistEntry,
  getAuditLog,
  provisionScreen,
  ProvisionedScreen,
  getTrials,
  addTrial,
  removeTrial
} from '@app/Settings/settings.api';
import { Pill } from '@app/Campaigns/badges';
import { relativeTime, formatDateTime } from '@app/Screens/format';
import { colors, radii, shadows } from '@app/components/designTokens';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 14px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  color: colors.subtle,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap'
};
const td: React.CSSProperties = {
  padding: '11px 14px',
  fontSize: 13.5,
  color: colors.body,
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle'
};

const actionTone = (action: string): 'green' | 'amber' | 'red' | 'gray' | 'blue' => {
  if (action.endsWith('_ENABLE') || action.endsWith('_ADD')) return 'green';
  if (action.endsWith('_DISABLE') || action.endsWith('_REMOVE')) return 'red';
  return 'blue';
};

const TIER_OPTIONS = ['BRONZE', 'SILVER', 'GOLD'];

const trialStatus = (t: TrialEntry): { text: string; tone: 'green' | 'amber' | 'red' | 'gray' | 'blue' } => {
  if (t.claimedBy != null) return { text: 'Claimed', tone: 'blue' };
  if (t.expiresAt && new Date(t.expiresAt).getTime() < Date.now()) return { text: 'Expired', tone: 'amber' };
  return { text: 'Available', tone: 'green' };
};

export const Settings: React.FunctionComponent = () => {
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [audit, setAudit] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newPattern, setNewPattern] = useState('');
  const [busy, setBusy] = useState(false);
  const [provisionName, setProvisionName] = useState('');
  const [provisioned, setProvisioned] = useState<ProvisionedScreen | null>(null);
  const [provisioning, setProvisioning] = useState(false);
  const [trials, setTrials] = useState<TrialEntry[]>([]);
  const [newTrialEmail, setNewTrialEmail] = useState('');
  const [newTrialTier, setNewTrialTier] = useState('GOLD');
  const [newTrialDays, setNewTrialDays] = useState(30);
  const [newTrialExpiry, setNewTrialExpiry] = useState(30);

  const load = useCallback(async (spinner: boolean) => {
    if (spinner) setLoading(true);
    try {
      const [list, en, log, trialList] = await Promise.all([
        getAllowlist(),
        getAllowlistEnabled(),
        getAuditLog(100),
        getTrials()
      ]);
      setEntries(list);
      setEnabled(en);
      setAudit(log);
      setTrials(trialList);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load settings');
    } finally {
      if (spinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  const onToggleEnabled = async () => {
    setBusy(true);
    try {
      const next = await setAllowlistEnabled(!enabled);
      setEnabled(next);
      toast(`Signup allowlist ${next ? 'enabled' : 'disabled'}`);
      load(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const onAdd = async () => {
    const pattern = newPattern.trim();
    if (!pattern) return;
    setBusy(true);
    try {
      await addAllowlistEntry(pattern);
      setNewPattern('');
      toast('Added to allowlist');
      load(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to add');
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (entry: AllowlistEntry) => {
    if (!window.confirm(`Remove "${entry.pattern}" from the signup allowlist?`)) return;
    setBusy(true);
    try {
      await removeAllowlistEntry(entry.id);
      toast('Removed from allowlist');
      load(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to remove');
    } finally {
      setBusy(false);
    }
  };

  const onProvision = async () => {
    const name = provisionName.trim();
    if (!name) return;
    setProvisioning(true);
    try {
      const result = await provisionScreen(name);
      setProvisioned(result);
      setProvisionName('');
      toast('Screen provisioned');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to provision screen');
    } finally {
      setProvisioning(false);
    }
  };

  const onAddTrial = async () => {
    const email = newTrialEmail.trim();
    if (!email) return;
    setBusy(true);
    try {
      await addTrial({ email, tier: newTrialTier, trialDays: newTrialDays, expiresInDays: newTrialExpiry });
      setNewTrialEmail('');
      toast(`Trial granted to ${email}`);
      load(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to add trial');
    } finally {
      setBusy(false);
    }
  };

  const onRemoveTrial = async (t: TrialEntry) => {
    if (!window.confirm(`Remove the trial for "${t.email}"?`)) return;
    setBusy(true);
    try {
      await removeTrial(t.id);
      toast('Trial removed');
      load(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to remove trial');
    } finally {
      setBusy(false);
    }
  };

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 },
    title: { fontSize: 22, fontWeight: 700, color: colors.title, margin: 0 },
    subtle: { fontSize: 13, color: colors.subtle },
    refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: `1px solid ${colors.border}`, borderRadius: radii.button, background: '#fff', color: colors.body, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    panel: { background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, boxShadow: shadows.softCard, padding: 20, marginBottom: 20 },
    panelTitle: { fontSize: 16, fontWeight: 700, color: colors.title, margin: '0 0 6px' },
    panelDesc: { fontSize: 13, color: colors.subtle, margin: '0 0 16px' },
    toggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', background: colors.bgMuted, borderRadius: radii.input, marginBottom: 16 },
    addRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
    input: { flex: 1, minWidth: 220, padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, outline: 'none' },
    addBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', border: 'none', borderRadius: radii.button, background: colors.accent, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
    iconBtn: { background: 'transparent', border: 'none', color: colors.danger, cursor: 'pointer', padding: 6, borderRadius: 6 },
    select: { padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, background: '#fff', cursor: 'pointer' },
    fieldLabel: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: colors.subtle, textTransform: 'uppercase', letterSpacing: '0.3px' },
    numInput: { width: 92, padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, outline: 'none' }
  };

  const switchBtn = (on: boolean) => (
    <button
      onClick={onToggleEnabled}
      disabled={busy}
      style={{
        width: 46,
        height: 26,
        borderRadius: 9999,
        border: 'none',
        background: on ? colors.accent : '#cbd5e1',
        position: 'relative',
        cursor: busy ? 'default' : 'pointer',
        transition: 'background 0.2s'
      }}
      aria-label="Toggle allowlist"
    >
      <span style={{ position: 'absolute', top: 3, left: on ? 23 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
    </button>
  );

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Settings</h1>
          <div style={styles.subtle}>Signup gating, trials, and the admin action audit trail.</div>
        </div>
        <button style={styles.refreshBtn} onClick={() => load(true)}>
          <SyncIcon className={loading ? 'logo-bolt-pulse' : undefined} /> Refresh
        </button>
      </div>

      {error && (
        <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 16 }}>{error}</div>
      )}

      {/* Signup allowlist */}
      <div style={styles.panel}>
        <h3 style={styles.panelTitle}>Signup allowlist</h3>
        <p style={styles.panelDesc}>
          When enabled, only emails matching an entry below can register. Patterns are a full email
          (<code>alice@example.com</code>) or a domain wildcard (<code>@example.com</code>).
        </p>

        <div style={styles.toggleRow}>
          <div>
            <div style={{ fontWeight: 600, color: colors.title }}>Allowlist enforcement</div>
            <div style={{ fontSize: 12, color: colors.subtle }}>{enabled ? 'On — signups are gated' : 'Off — anyone can sign up'}</div>
          </div>
          {switchBtn(enabled)}
        </div>

        <div style={styles.addRow}>
          <input
            style={styles.input}
            placeholder="alice@example.com or @example.com"
            value={newPattern}
            onChange={e => setNewPattern(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onAdd()}
          />
          <button style={styles.addBtn} onClick={onAdd} disabled={busy || !newPattern.trim()}>
            <PlusCircleIcon /> Add
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: colors.subtle }}>Loading…</div>
        ) : entries.length === 0 ? (
          <div style={{ padding: 16, color: colors.placeholder, fontSize: 14 }}>No allowlist entries.</div>
        ) : (
          <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: radii.input }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Pattern</th>
                  <th style={th}>Added by</th>
                  <th style={th}>Added</th>
                  <th style={{ ...th, textAlign: 'right' }}>Remove</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id}>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{e.pattern}</td>
                    <td style={td}>{e.createdBy || '—'}</td>
                    <td style={td} title={formatDateTime(e.createdAt)}>{relativeTime(e.createdAt)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button style={styles.iconBtn} onClick={() => onRemove(e)} disabled={busy} aria-label="Remove">
                        <TrashIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Provision a screen */}
      <div style={styles.panel}>
        <h3 style={styles.panelTitle}>Provision a screen</h3>
        <p style={styles.panelDesc}>
          Creates a <code>ROLE_SCREEN</code> credential for a new ad display. The client secret is
          shown <strong>once</strong> — copy it into the device now; it can&apos;t be retrieved later.
        </p>
        <div style={styles.addRow}>
          <input
            style={styles.input}
            placeholder="Screen display name, e.g. Lobby Pi #3"
            value={provisionName}
            onChange={e => setProvisionName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onProvision()}
          />
          <button style={styles.addBtn} onClick={onProvision} disabled={provisioning || !provisionName.trim()}>
            <PlusCircleIcon /> Provision
          </button>
        </div>
        {provisioned && (
          <div style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.3)', borderRadius: radii.input, padding: 14 }}>
            <div style={{ fontWeight: 700, color: '#047857', marginBottom: 10 }}>Screen credentials — copy now, shown once</div>
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              <span style={{ color: colors.subtle }}>Client ID:</span>{' '}
              <code style={{ fontFamily: 'monospace' }}>{provisioned.clientId}</code>
            </div>
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              <span style={{ color: colors.subtle }}>Client secret:</span>{' '}
              <code style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{provisioned.clientSecret}</code>
            </div>
            <button style={styles.refreshBtn} onClick={() => setProvisioned(null)}>
              Done — I&apos;ve saved it
            </button>
          </div>
        )}
      </div>

      {/* Trials */}
      <div style={styles.panel}>
        <h3 style={styles.panelTitle}>Trials</h3>
        <p style={styles.panelDesc}>
          Grant a customer a one-shot free trial of a tier. The user must already have an account —
          they redeem it on the plan page. <strong>Expires in</strong> is the window to <em>start</em>{' '}
          the trial, not its length.
        </p>

        <div style={styles.addRow}>
          <input
            style={styles.input}
            placeholder="customer@example.com"
            value={newTrialEmail}
            onChange={e => setNewTrialEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onAddTrial()}
          />
          <select style={styles.select} value={newTrialTier} onChange={e => setNewTrialTier(e.target.value)} aria-label="Tier">
            {TIER_OPTIONS.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <label style={styles.fieldLabel}>
            Trial days
            <input
              style={styles.numInput}
              type="number"
              min={1}
              max={365}
              value={newTrialDays}
              onChange={e => setNewTrialDays(Number(e.target.value))}
            />
          </label>
          <label style={styles.fieldLabel}>
            Expires in (days)
            <input
              style={styles.numInput}
              type="number"
              min={1}
              max={365}
              value={newTrialExpiry}
              onChange={e => setNewTrialExpiry(Number(e.target.value))}
            />
          </label>
          <button style={styles.addBtn} onClick={onAddTrial} disabled={busy || !newTrialEmail.trim()}>
            <PlusCircleIcon /> Grant
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: colors.subtle }}>Loading…</div>
        ) : trials.length === 0 ? (
          <div style={{ padding: 16, color: colors.placeholder, fontSize: 14 }}>No trials granted.</div>
        ) : (
          <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: radii.input }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={th}>Email</th>
                  <th style={th}>Tier</th>
                  <th style={th}>Trial days</th>
                  <th style={th}>Status</th>
                  <th style={th}>Expires</th>
                  <th style={th}>Added by</th>
                  <th style={{ ...th, textAlign: 'right' }}>Remove</th>
                </tr>
              </thead>
              <tbody>
                {trials.map(t => {
                  const s = trialStatus(t);
                  return (
                    <tr key={t.id}>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{t.email}</td>
                      <td style={td}>{t.tier}</td>
                      <td style={td}>{t.trialDays}</td>
                      <td style={td}>
                        <Pill text={s.text} tone={s.tone} />
                      </td>
                      <td style={td} title={formatDateTime(t.expiresAt)}>{relativeTime(t.expiresAt)}</td>
                      <td style={td}>{t.createdBy || '—'}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button style={styles.iconBtn} onClick={() => onRemoveTrial(t)} disabled={busy} aria-label="Remove">
                          <TrashIcon />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Audit log */}
      <div style={styles.panel}>
        <h3 style={styles.panelTitle}>Audit log</h3>
        <p style={styles.panelDesc}>Recent admin actions (screen enable/disable, allowlist changes).</p>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: colors.subtle }}>Loading…</div>
        ) : audit.length === 0 ? (
          <div style={{ padding: 16, color: colors.placeholder, fontSize: 14 }}>No admin actions recorded yet.</div>
        ) : (
          <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: radii.input }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={th}>When</th>
                  <th style={th}>Admin</th>
                  <th style={th}>Action</th>
                  <th style={th}>Target</th>
                  <th style={th}>Details</th>
                </tr>
              </thead>
              <tbody>
                {audit.map(a => (
                  <tr key={a.id}>
                    <td style={td} title={formatDateTime(a.createdAt)}>{relativeTime(a.createdAt)}</td>
                    <td style={td}>{a.actorEmail || <span style={{ color: colors.placeholder }}>system</span>}</td>
                    <td style={td}>
                      <Pill text={a.action} tone={actionTone(a.action)} />
                    </td>
                    <td style={td}>
                      {a.targetType ? `${a.targetType}${a.targetId ? ` #${a.targetId}` : ''}` : '—'}
                    </td>
                    <td style={{ ...td, color: colors.subtle }}>{a.details || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
